import { makeAccountInput } from '@/modules/entities/EntitySubforms/utils/shared/accounts.utils';
import { nonTrustEntityTaxStatusToApiTypeMap } from '@/modules/entities/EntitySubforms/utils/shared/taxStatus.utils';
import { getTaxStatusValuesFromEntityType } from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  AccessParameterKind,
  AugmentedCreateBeneficiaryInput,
  AugmentedCreateEntityInput,
  EntityInEstateStatus,
} from '@/types/schema';

import {
  CREATE_ENTITY_SHORT_FORM_NAMESPACE,
  EntityShortFormShape,
} from '../CreateEntityShortForm.types';
import {
  getCreateEntityInputCommonProps,
  getCreateSubtypeInputCommonProps,
  getSinglePrincipalInput,
  MakeCreateEntityInputParams,
  makeCreateMultiplePrincipalInputs,
} from './CreateEntityShortForm.common';

export function makeCreateIndividualAccountInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const ownerInput = getSinglePrincipalInput(formInput, householdId);
  return {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withIndividualPersonalAccount: {
      create: {
        ...getCreatePersonalAccountInputCommonProps(formInput, householdId),
      },
      withDesignerAccount: makeAccountInput(formInput, householdId),
      withOwner: ownerInput,
    },
  };
}

export function makeCreateJointAccountInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  return {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withJointPersonalAccount: {
      create: {
        ...getCreatePersonalAccountInputCommonProps(formInput, householdId),
      },
      withOwners: makeCreateMultiplePrincipalInputs(formInput, householdId),
      withDesignerAccount: makeAccountInput(formInput, householdId),
    },
  };
}

export function makeCreateRetirementAccountInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  return {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withRetirementPersonalAccount: {
      create: {
        ...getCreatePersonalAccountInputCommonProps(formInput, householdId),
      },
      withOwner: getSinglePrincipalInput(formInput, householdId),
      withDesignerAccount: makeAccountInput(formInput, householdId),
      // The short form doesn't collect beneficiaries, but household creation
      // (CreateSingleOwnerAccountInputs) seeds retirement accounts with a default
      // current-access beneficiary: the owner with FULL access. Mirror that here so
      // accounts created via AddNewHoldingsGroup aren't left with zero beneficiaries.
      withBeneficiaries: makeDefaultCurrentAccessBeneficiaryInputs(
        formInput,
        householdId
      ),
    },
  };
}

/**
 * Builds the default current-access beneficiary for an owner-held account: the
 * owner with a single FULL access parameter. Matches the backend default applied
 * during household creation. Returns an empty array if no owner is selected.
 */
function makeDefaultCurrentAccessBeneficiaryInputs(
  formInput: EntityShortFormShape,
  householdId: string
): AugmentedCreateBeneficiaryInput[] {
  const ownerId =
    formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].selectedSinglePrincipal;
  if (!ownerId) {
    return [];
  }

  return [
    {
      create: {
        householdID: householdId,
        individualID: ownerId,
      },
      withAccessParameters: [
        {
          create: {
            householdID: householdId,
            kind: AccessParameterKind.Full,
          },
        },
      ],
    },
  ];
}

export function makeCreateCustodialAccountInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const ownerInput = getSinglePrincipalInput(formInput, householdId);
  return {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withCustodialPersonalAccount: {
      create: {
        ...getCreatePersonalAccountInputCommonProps(formInput, householdId),
      },
      withDesignerAccount: makeAccountInput(formInput, householdId),
      withOwner: ownerInput,
    },
  };
}

export function makeCreateQualifiedTuitionAccountInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const ownerInput = getSinglePrincipalInput(formInput, householdId);

  return {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withQualifiedTuitionPersonalAccount: {
      create: {
        ...getCreatePersonalAccountInputCommonProps(formInput, householdId),
      },
      withDesignerAccount: makeAccountInput(formInput, householdId),
      withOwner: ownerInput,
    },
  };
}

export function makeCreateInsuranceAccountInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  return {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withInsurancePersonalAccount: {
      create: {
        ...getCreatePersonalAccountInputCommonProps(formInput, householdId),
        inEstateStatus: EntityInEstateStatus.InEstate,
        stateInEstateStatus: EntityInEstateStatus.InEstate,
      },
      withDesignerAccount: makeAccountInput(formInput, householdId),
      withOwners: makeCreateMultiplePrincipalInputs(formInput, householdId),
    },
  };
}

function getCreatePersonalAccountInputCommonProps(
  formInput: EntityShortFormShape,
  householdId: string
) {
  const { nonTrustEntityTaxStatus } = getTaxStatusValuesFromEntityType(
    formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].entityType as EntityType
  );

  return {
    ...getCreateSubtypeInputCommonProps(formInput, householdId),
    taxStatus: nonTrustEntityTaxStatus
      ? nonTrustEntityTaxStatusToApiTypeMap[nonTrustEntityTaxStatus]
      : undefined,
  };
}
